import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Input
} from '@mui/material';
import {
  Upload as UploadIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import {
  getSystemStatus,
  initializeCompatibilitySystem,
  importFromCSV,
  getCableTypeConnectors
} from '../services/compatibilitySystemV2';

export default function CompatibilityMatrixManager({ open, onClose }) {
  const [systemStatus, setSystemStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewData, setPreviewData] = useState(null);

  useEffect(() => {
    if (open) {
      loadSystemStatus();
    }
  }, [open]);

  const loadSystemStatus = () => {
    const status = getSystemStatus();
    setSystemStatus(status);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const fileContent = await file.text();
      const result = await importFromCSV(fileContent, file.name);
      
      if (result.success) {
        setSuccess(`Successfully imported compatibility matrix from ${file.name}`);
        loadSystemStatus();
        
        // Generate preview data from the imported results
        const preview = [];
        const cableTypes = ['audio', 'power', 'dmx', 'speaker', 'hybrid'];
        
        for (const cableType of cableTypes) {
          try {
            const connectors = await getCableTypeConnectors(cableType);
            if (connectors && connectors.length > 0) {
              preview.push({
                cableType,
                connectorCount: connectors.length,
                sampleConnectors: connectors.slice(0, 3).join(', ') + (connectors.length > 3 ? '...' : '')
              });
            }
          } catch (err) {
            console.warn(`Error getting connectors for ${cableType}:`, err);
          }
        }
        
        setPreviewData(preview);
      } else {
        setError(`Error importing file: ${result.error || 'Unknown error'}`);
      }
      
    } catch (err) {
      setError(`Error importing file: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadDefault = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      initializeCompatibilitySystem();
      setSuccess('Successfully loaded default compatibility matrix');
      loadSystemStatus();
    } catch (err) {
      setError(`Error loading default matrix: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    try {
      initializeCompatibilitySystem();
      setSuccess('Compatibility matrix has been reset to defaults');
      setPreviewData(null);
      loadSystemStatus();
    } catch (err) {
      setError(`Error resetting matrix: ${err.message}`);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon color="success" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'error':
        return <WarningIcon color="error" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'info';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon />
          Compatibility Matrix Manager V2
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" paragraph>
            The compatibility matrix defines which connectors can be used together in cables. 
            Import a CSV file to update the compatibility rules used throughout the cable builder.
          </Typography>
        </Box>

        {/* Current Status */}
        {systemStatus && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                {getStatusIcon(systemStatus.status)}
                <Typography variant="h6">Current Status</Typography>
              </Box>
              <Alert severity={getStatusColor(systemStatus.status)} sx={{ mb: 2 }}>
                {systemStatus.message || 'System operational'}
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Version: {systemStatus.version} | Last Updated: {new Date(systemStatus.lastUpdated).toLocaleString()}
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">System Information:</Typography>
                <Typography variant="body2">
                  Connectors: {systemStatus.connectorCount || 0}
                </Typography>
                <Typography variant="body2">
                  Cable Types: {systemStatus.cableTypeCount || 0}
                </Typography>
                <Typography variant="body2">
                  Wire Gauges: {systemStatus.wireGaugeCount || 0}
                </Typography>
                <Typography variant="body2">
                  Data Sources: {systemStatus.sources?.join(', ') || 'Default'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* File Upload */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Import Compatibility Matrix
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                disabled={loading}
                startAdornment={<UploadIcon sx={{ mr: 1 }} />}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleLoadDefault}
                  disabled={loading}
                >
                  Load Default Matrix
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={handleReset}
                  disabled={loading}
                >
                  Reset Matrix
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Loading */}
        {loading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Processing compatibility matrix...
            </Typography>
          </Box>
        )}

        {/* Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Preview Data */}
        {previewData && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Import Preview (Cable Types)
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Cable Type</TableCell>
                      <TableCell>Connector Count</TableCell>
                      <TableCell>Sample Connectors</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Chip label={row.cableType} size="small" />
                        </TableCell>
                        <TableCell>{row.connectorCount}</TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {row.sampleConnectors}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
} 